import { spawn } from "child_process";
import * as path from "path";

import { ensureDir, outputFile, pathExists } from "fs-extra";
import pLimit from "p-limit";

import { ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { ObservableValueProducerBase } from "../../Commons/TasksCore/ChainableValueProducer";
import { FormGenerator } from "../Form/GenerateForm";
import { reject } from "../../../../Common/TypingUtils";
import { defaultRegistry } from "../../Commons/Defaults";

import { createAsyncGenerator } from "./GeneratorWorker/CreateAsyncGenerator";

export class AcquirePublishedGeneratorTask extends ObservableValueProducerBase<FormGenerator> {
    private readonly logger: ILogger;
    private readonly version: string;
    private readonly cacheDirectory: string;
    private acquiredGenerator?: FormGenerator;
    private readonly useVersionFromCache: boolean;
    private readonly acquireLimit = pLimit(1);

    public constructor(
        version: string,
        candyModulesDirectory: string,
        useVersionFromCache: boolean,
        logger: ILogger = getLogger()
    ) {
        super();
        this.logger = logger.withPrefix("GENERATOR");
        this.version = version;
        this.cacheDirectory = candyModulesDirectory;
        this.useVersionFromCache = useVersionFromCache;
    }

    public async getCurrentValue(): Promise<FormGenerator> {
        if (this.acquiredGenerator == undefined) {
            await this.acquireLimit(async () => {
                if (this.acquiredGenerator == undefined) {
                    const generatorDirectory = path.join(this.cacheDirectory, "generators", this.version);
                    const useGeneratorFromCache = this.useVersionFromCache && (await pathExists(generatorDirectory));

                    if (!useGeneratorFromCache) {
                        await this.prepareGeneratorDirectory(generatorDirectory);
                        this.logger.log(`Acquiring generator ${this.version}`);
                        await this.execute(generatorDirectory, `npm install @kontur.candy/generator@${this.version}`);
                    }

                    const result = createAsyncGenerator(
                        path.join(generatorDirectory, "node_modules", "@kontur.candy", "generator")
                    );
                    this.acquiredGenerator = result;
                }
            });
        }
        if (this.acquiredGenerator == undefined) {
            throw new Error("InvalidProgramState");
        }
        return this.acquiredGenerator;
    }

    protected async beginListening(): Promise<void> {
        // Нет предков и тут нечего наблюдать
    }

    protected async stopListening(): Promise<void> {
        // Нет предков и тут нечего наблюдать
    }

    private async prepareGeneratorDirectory(generatorDirectory: string): Promise<void> {
        await ensureDir(generatorDirectory);
        await outputFile(
            path.resolve(generatorDirectory, "package.json"),
            `{ "name": "generator", "version": "1.0.0", "description": "Generator", "author": "" }`
        );
    }

    private execute(generatorDirectory: string, command: string): Promise<void> {
        const workingDirectory = generatorDirectory;
        const [executable, ...executableArguments] = command.split(" ");
        return new Promise<void>((resolve, rejectPromise) => {
            const { npm_config_registry: _, ...env } = process.env;
            const watchProcess = spawn(executable ?? reject(), executableArguments, {
                cwd: workingDirectory,
                shell: true,
                windowsHide: true,
                stdio: "pipe",
                env: {
                    ...env,
                    NPM_CONFIG_REGISTRY: defaultRegistry,
                },
            });
            watchProcess.stdout.on("data", (data: Buffer | string) => {
                this.logger.verbose(data.toString());
            });
            watchProcess.stderr.on("data", (data: Buffer | string) => {
                this.logger.verbose(data.toString());
            });
            watchProcess.on("exit", code => {
                if (code !== 0) {
                    rejectPromise(new Error(`Command ${command} exited with code ${code}`));
                } else {
                    resolve();
                }
            });
        });
    }
}
